
import { supabase } from '@/integrations/supabase/client';
import { PendingImageUpload } from '@/types/chat';

export interface ImageUploadResponse {
  success: boolean;
  urls?: string[];
  imageData?: Array<{
    url: string;
    originalFilename: string;
    fileSize: number;
    mimeType: string;
    displayOrder: number;
  }>;
  error?: string;
}

export interface SendMessageWithImagesRequest {
  conversationId: string;
  content: string;
  receiverId: string;
  imageData?: Array<{
    url: string;
    originalFilename: string;
    fileSize: number;
    mimeType: string;
    displayOrder: number;
  }>;
}

// Helper function to convert File to base64
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
}

export const chatImageService = {
  async uploadImages(
    conversationId: string,
    files: File[]
  ): Promise<ImageUploadResponse> {
    try {
      // Convert files to base64 for Edge Function
      const filesData = await Promise.all(
        files.map(async (file, index) => ({
          data: await fileToBase64(file),
          originalFilename: file.name,
          fileSize: file.size,
          mimeType: file.type,
          displayOrder: index + 1
        }))
      );

      const { data, error } = await supabase.functions.invoke('upload-chat-images', {
        body: {
          conversationId,
          files: filesData
        }
      });

      if (error) {
        console.error('Image upload error:', error);
        return {
          success: false,
          error: error.message || 'Failed to upload images'
        };
      }

      return data as ImageUploadResponse;
    } catch (error) {
      console.error('Image upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  async sendMessageWithImages(
    request: SendMessageWithImagesRequest
  ): Promise<{ success: boolean; message?: any; error?: string }> {
    try {
      const { data, error } = await supabase.functions.invoke('send-message-with-images', {
        body: request
      });

      if (error) {
        console.error('Send message with images error:', error);
        return {
          success: false,
          error: error.message || 'Failed to send message'
        };
      }

      return data as { success: boolean; message?: any; error?: string };
    } catch (error) {
      console.error('Send message with images error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  validateFiles(files: File[]): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const maxFiles = 3;
    const maxFileSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    if (files.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} images allowed`);
    }

    files.forEach((file, index) => {
      if (!allowedTypes.includes(file.type)) {
        errors.push(`Image ${index + 1}: Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed`);
      }
      
      if (file.size > maxFileSize) {
        errors.push(`Image ${index + 1}: File size exceeds 5MB limit`);
      }
    });

    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > 15 * 1024 * 1024) {
      errors.push('Total file size exceeds 15MB limit');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  },

  createPreviewUrl(file: File): string {
    return URL.createObjectURL(file);
  },

  revokePreviewUrl(url: string): void {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }
};
